import { FC, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { Modal, SnackbarAlert } from "@/core/components"
import { formatDateAndTime, getApiErrorMessage } from "@/core/utils"
import { store } from "@core/store"
import { GuestBot } from "../../interfaces"
import {
  guestBotApi,
  useCancelJoinJobMutation,
  useGetJoinJobQuery,
} from "../../store"
import { useGuestBotWriteAccess } from "../../hooks"
import {
  CHATS_POLL_MS,
  JOIN_JOB_STATUS_COLOR,
  JOIN_JOB_STATUS_LABEL,
  JOIN_LINK_STATUS_COLOR,
  JOIN_LINK_STATUS_LABEL,
  isJoinJobTerminal,
} from "../../utils/chatStatus"

const LINK_STATUS_ORDER: GuestBot.JoinLinkStatus[] = [
  "pending_join",
  "joined",
  "approval_pending",
  "failed",
  "cancelled",
]
const ALWAYS_VISIBLE: GuestBot.JoinLinkStatus[] = ["joined", "approval_pending"]

interface IProps {
  jobId: string
}

export const JoinJobProgress: FC<IProps> = ({ jobId }) => {
  const canWrite = useGuestBotWriteAccess()
  const navigate = useNavigate()
  const [pollingInterval, setPollingInterval] = useState(CHATS_POLL_MS)
  const { data, isFetching, isError, error } = useGetJoinJobQuery(
    { pathParams: { jobId } },
    { pollingInterval },
  )
  const [cancelJoinJob, { isLoading: isCancelling }] =
    useCancelJoinJobMutation()
  const [cancelOpen, setCancelOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)
  const invalidatedRef = useRef(false)

  // Poll only while non-terminal (spec §7). Also stop on query error: a
  // deleted job must not keep retrying behind the Alert. No optimistic
  // terminal writes. awaiting_approval is non-terminal — keeps polling (D-6).
  useEffect(() => {
    if (isError) {
      setPollingInterval(0)
      return
    }
    if (data && isJoinJobTerminal(data.status)) setPollingInterval(0)
  }, [data, isError])

  // F9: on the FIRST terminal poll, refresh the (possibly already-mounted)
  // pool view with newly joined chats. One-shot via ref.
  useEffect(() => {
    if (data && isJoinJobTerminal(data.status) && !invalidatedRef.current) {
      invalidatedRef.current = true
      store.dispatch(guestBotApi.util.invalidateTags(["Chats"]))
    }
  }, [data])

  const counts = useMemo(() => {
    const result = new Map<GuestBot.JoinLinkStatus, number>()
    ;(data?.items ?? []).forEach((l) =>
      result.set(l.status, (result.get(l.status) ?? 0) + 1),
    )
    return result
  }, [data])

  const links = useMemo(
    () => [...(data?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [data],
  )

  const goBack = () => navigate("/telegram/guest-bots/chats")

  if (isError)
    return (
      <Stack spacing={2}>
        <Alert severity="error">{getApiErrorMessage(error)}</Alert>
        <Button onClick={goBack}>Назад к списку</Button>
      </Stack>
    )
  if (!data) return isFetching ? <CircularProgress size={24} /> : null

  const isTerminal = isJoinJobTerminal(data.status)

  const handleCancel = async () => {
    try {
      await cancelJoinJob({ pathParams: { jobId } }).unwrap()
      setSnackbar({ variant: "success", message: "Вступление отменено" })
    } catch (e) {
      setSnackbar({ variant: "error", message: getApiErrorMessage(e) })
    } finally {
      setCancelOpen(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Chip
          size="small"
          label={JOIN_JOB_STATUS_LABEL[data.status]}
          color={JOIN_JOB_STATUS_COLOR[data.status]}
        />
        {LINK_STATUS_ORDER.filter(
          (s) => ALWAYS_VISIBLE.includes(s) || (counts.get(s) ?? 0) > 0,
        ).map((s) => (
          <Chip
            key={s}
            size="small"
            variant="outlined"
            label={`${JOIN_LINK_STATUS_LABEL[s]}: ${counts.get(s) ?? 0}`}
          />
        ))}
      </Box>

      {data.status === "failed" && data.lastError && (
        <Alert severity="error">{data.lastError}</Alert>
      )}

      {canWrite && (
        <Box>
          <Button
            variant="outlined"
            color="error"
            disabled={isTerminal || isCancelling}
            onClick={() => setCancelOpen(true)}
          >
            Отменить
          </Button>
        </Box>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ссылка</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Причина</TableCell>
            <TableCell>Chat ID</TableCell>
            <TableCell>Вступление</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {links.map((l) => (
            <TableRow key={l.id}>
              <TableCell>{l.url}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={JOIN_LINK_STATUS_LABEL[l.status]}
                  color={JOIN_LINK_STATUS_COLOR[l.status]}
                />
              </TableCell>
              <TableCell>{l.reason ?? "—"}</TableCell>
              <TableCell>{l.chatId ?? "—"}</TableCell>
              <TableCell>
                {l.joinedAt ? formatDateAndTime(l.joinedAt) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box display="flex" gap={1}>
        <Button onClick={goBack}>Назад к списку</Button>
      </Box>

      {cancelOpen && (
        <Modal
          disableBackdropClose
          title="Отменить вступление?"
          onClose={() => setCancelOpen(false)}
          element={
            <Box>
              <Typography>
                Ожидающие ссылки будут отменены. Уже выполненные действия не
                изменятся.
              </Typography>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={() => setCancelOpen(false)}>
                  Не отменять
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  disabled={isCancelling}
                  onClick={handleCancel}
                >
                  Отменить вступление
                </Button>
              </Box>
            </Box>
          }
        />
      )}
      <SnackbarAlert
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
        onClose={() => setSnackbar(null)}
      />
    </Stack>
  )
}
